import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CampaignSavedData, JobTitle } from "../../../../../types/apiTypes";
import { PlusIcon } from "../../../../images";
import { CustomButton } from "../../../../ui/CustomButton";
import { CustomToast } from "../../../../ui/CustomToast";
import { JobTitlesSortableList } from "../../../../features/JobTitlesSortableList";

const Header = ({
  count,
  addTitleHandler,
}: {
  count: number;
  addTitleHandler: () => void;
}) => {
  const isLimit = count > 24;
  return (
    <Stack gap="16px">
      <Stack
        flexDirection="row"
        gap="32px"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack gap="4px">
          <Typography variant="h4" color="custom.textDarkBlue">
            Define Target Job Titles
          </Typography>
          <Typography variant="button" color="#00000099">
            Max 25 items
          </Typography>
        </Stack>
        <CustomButton
          onClick={addTitleHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          startIcon={!isLimit && <PlusIcon />}
          sx={{
            height: "42px",
            borderColor: "#1D3E70",
            flexGrow: 1,
            width: "100%",
            maxWidth: "50%",
          }}
          disabled={isLimit}
        >
          {isLimit ? "Limit Reached" : "Add Job Title"}
        </CustomButton>
      </Stack>
      <Typography
        variant="body2"
        color="custom.textDarkBlue"
        sx={{
          border: "1px solid #FFD5A1",
          backgroundColor: "#FFF7E5",
          borderRadius: "6px",
          padding: "16px",
        }}
      >
        Adjust and prioritize the list of key titles that should be approached
        in the first place. You will be able to expand it later in campaign
        settings.
      </Typography>
    </Stack>
  );
};

export const DefineTargetJobTitlesStep = ({
  initData,
  backHandler,
  nextStepHandler,
  setResultData,
}: {
  initData: CampaignSavedData;
  backHandler: () => void;
  nextStepHandler: () => void;
  setResultData: React.Dispatch<React.SetStateAction<CampaignSavedData>>;
}) => {
  const [titles, setTitles] = useState<JobTitle[]>(
    initData.target_audiences || [],
  );
  const [createdTitleId, setCreatedTitleId] = useState<string>("");

  useEffect(() => {
    setResultData((prev) => ({
      ...prev,
      target_audiences: titles,
    }));
  }, [titles]);

  const addTitleHandler = () => {
    const id = Date.now().toString();
    setTitles([...titles, { id, title: "", is_critical: true, is_edit: true }]);
    setCreatedTitleId(id);
  };

  const submitHandler = () => {
    if (titles.some(({ is_edit }) => is_edit)) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Please complete editing before proceeding."
        />,
      );
      return;
    }

    setResultData((prev) => ({ ...prev, target_audiences: titles }));
    nextStepHandler();
  };

  const goBackHandler = () => {
    setResultData((prev) => ({ ...prev, target_audiences: titles }));
    backHandler();
  };

  const isNoTitles = titles.length === 0;
  return (
    <Stack gap="24px" width="100%">
      <Header count={titles.length} addTitleHandler={addTitleHandler} />
      {!isNoTitles && (
        <JobTitlesSortableList
          titles={titles}
          setTitles={setTitles}
          createdTitleId={createdTitleId}
          setCreatedTitleId={setCreatedTitleId}
        />
      )}

      <Stack direction="row" gap="8px">
        <CustomButton
          onClick={goBackHandler}
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={submitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          disabled={isNoTitles}
        >
          Next Step
        </CustomButton>
      </Stack>
    </Stack>
  );
};
